import React, { useState } from 'react';
import { monthLabelFromPeriod } from '../../utils/groupByMonthYear';
import { formatMonthIssuedAmounts } from '../../utils/documentCurrency';

/**
 * Tabla agrupada por mes: muestra resúmenes y carga el detalle al expandir.
 */
const LazyTableWithMonthGroups = ({
  months,
  getMonthItems,
  isMonthLoading,
  onMonthExpand,
  renderRow,
  headerRow,
  colgroup = null,
  columnCount,
  defaultCollapsed = true,
  className = '',
  wrapperClassName = 'overflow-x-auto',
  groupHeaderClassName = 'px-6 py-2.5 text-sm font-semibold text-gray-700',
  emptyMessage = 'No hay registros',
  hideHeaderUntilExpanded = true,
}) => {
  const [collapsed, setCollapsed] = useState({});

  const isCollapsed = (key) => {
    if (defaultCollapsed && collapsed[key] === undefined) {
      return true;
    }
    return !!collapsed[key];
  };

  const toggle = async (period) => {
    const wasCollapsed = isCollapsed(period);
    if (wasCollapsed) {
      await onMonthExpand(period);
      setCollapsed((prev) => ({ ...prev, [period]: false }));
      return;
    }
    setCollapsed((prev) => ({ ...prev, [period]: true }));
  };

  const hasExpandedMonth = months?.some((month) => !isCollapsed(month.period));

  if (!months?.length) {
    return (
      <div className={wrapperClassName}>
        <table className={`w-full divide-y divide-gray-200 ${className}`}>
          {colgroup}
          {!hideHeaderUntilExpanded && <thead className="bg-gray-50">{headerRow}</thead>}
          <tbody>
            <tr>
              <td colSpan={columnCount} className="px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <table className={`w-full divide-y divide-gray-200 ${className}`}>
        {colgroup}
        {(!hideHeaderUntilExpanded || hasExpandedMonth) && (
          <thead className="bg-gray-50">{headerRow}</thead>
        )}
        <tbody className="bg-white divide-y divide-gray-200">
          {months.map((month) => {
            const period = month.period;
            const collapsedState = isCollapsed(period);
            const loading = isMonthLoading(period);
            const items = getMonthItems(period) || [];
            const count = month.count ?? items.length;
            const label = monthLabelFromPeriod(period);
            const issuedAmountsLabel = formatMonthIssuedAmounts(month);

            return (
              <React.Fragment key={period}>
                <tr
                  className="bg-gray-100/80 hover:bg-gray-100 border-y border-gray-200"
                  onClick={() => !loading && toggle(period)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !loading) {
                      e.preventDefault();
                      toggle(period);
                    }
                  }}
                  aria-expanded={!collapsedState}
                >
                  <td colSpan={columnCount} className={groupHeaderClassName}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded" aria-hidden>
                        {loading ? (
                          <span className="inline-block w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                        ) : collapsedState ? (
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </span>
                      <span>{label}</span>
                      <span className="text-gray-500 font-normal">
                        ({count} {count === 1 ? 'registro' : 'registros'})
                      </span>
                      {issuedAmountsLabel ? (
                        <span className="text-gray-500 font-normal text-xs">· {issuedAmountsLabel}</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
                {!collapsedState && !loading && items.map((item) => renderRow(item))}
                {!collapsedState && loading && (
                  <tr>
                    <td colSpan={columnCount} className="px-6 py-4 text-sm text-gray-500 text-center">
                      Cargando documentos del mes…
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LazyTableWithMonthGroups;
